/**
 * WebSocket client for agent invocations.
 * Supports long-running connections (up to 2 hours) for heavy workloads.
 *
 * Auth: Cognito id_token passed on the WS URL query string. The JWT is
 * fetched just-in-time from /auth/ws-token (cookie-authenticated; the
 * cookie is HttpOnly so JS only sees the token briefly during this
 * single fetch). The Lambda Authorizer attached to $connect validates
 * the JWT against the Cognito JWKS — same trust chain as the HTTP path.
 *
 * The api_key path is no longer used by the browser. Service-to-service
 * callers can still pass `?api_key=` and the authorizer will accept it,
 * but the SPA never holds an api_key.
 */

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT || 'http://localhost:8080';

async function fetchIdTokenForWs() {
  const url = `${API_ENDPOINT.replace(/\/$/, '')}/auth/ws-token`;
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Could not get ws token: HTTP ${res.status}`);
  }
  const body = await res.json();
  return body.id_token;
}

export class AgentWebSocket {
  constructor(endpoint) {
    this.endpoint = endpoint;
    this.ws = null;
    this.messageHandlers = [];
    this.errorHandlers = [];
    this.closeHandlers = [];
  }

  /**
   * Connect to WebSocket server with authentication
   */
  async connect() {
    // Convert https:// to wss://
    let wsEndpoint = this.endpoint.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');

    let idToken;
    try {
      idToken = await fetchIdTokenForWs();
    } catch (err) {
      console.error('[WebSocket] failed to obtain id_token for connect:', err);
      throw err;
    }

    const separator = wsEndpoint.includes('?') ? '&' : '?';
    wsEndpoint = `${wsEndpoint}${separator}id_token=${encodeURIComponent(idToken)}`;
    console.log(`Connecting to WebSocket with Cognito id_token: ${wsEndpoint.replace(/id_token=[^&]+/, 'id_token=***')}`);

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(wsEndpoint);

        this.ws.onopen = () => {
          console.log('WebSocket connected');
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('WebSocket message received:', data);
            this.messageHandlers.forEach(handler => handler(data));
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.errorHandlers.forEach(handler => handler(error));
        };

        this.ws.onclose = (event) => {
          console.log('WebSocket closed:', event);
          this.closeHandlers.forEach(handler => handler(event));
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Send a message through WebSocket
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.error('WebSocket is not open. ReadyState:', this.ws?.readyState);
      throw new Error('WebSocket is not connected');
    }
  }

  async invokeAgent(agentId, prompt, requestId, onMessage, onError, onComplete, conversationId = null) {
    try {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        await this.connect();
      }

      const messageHandler = (data) => {
        console.log('[HANDLER] Processing message:', data.type, 'conv:', data.conversation_id, 'handlers:', this.messageHandlers.length);
        switch (data.type) {
          case 'start':
            console.log('Agent invocation started:', data);
            break;
          case 'message':
            if (onMessage) onMessage(data);
            break;
          case 'tool_start':
            if (onMessage) onMessage(data);
            break;
          case 'tool_end':
            if (onMessage) onMessage(data);
            break;
          case 'complete': {
            console.log('Agent invocation completed:', data);
            const completeIndex = this.messageHandlers.indexOf(messageHandler);
            if (completeIndex > -1) this.messageHandlers.splice(completeIndex, 1);
            if (onComplete) onComplete(data);
            break;
          }
          case 'error': {
            console.error('Agent error:', data.message);
            const errorIndex = this.messageHandlers.indexOf(messageHandler);
            if (errorIndex > -1) this.messageHandlers.splice(errorIndex, 1);
            if (onError) onError(new Error(data.message));
            break;
          }
          default:
            console.warn('Unknown message type:', data.type);
        }
      };

      this.messageHandlers.push(messageHandler);

      const payload = {
        action: 'invoke',
        agent_id: agentId,
        request_id: requestId,
        input: { prompt },
      };
      if (conversationId) payload.conversation_id = conversationId;

      console.log('[WS CLIENT] Sending payload:', payload);
      this.send(payload);
    } catch (error) {
      console.error('Error invoking agent:', error);
      if (onError) onError(error);
      throw error;
    }
  }

  onMessage(handler) {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) this.messageHandlers.splice(index, 1);
    };
  }

  onError(handler) {
    this.errorHandlers.push(handler);
    return () => {
      const index = this.errorHandlers.indexOf(handler);
      if (index > -1) this.errorHandlers.splice(index, 1);
    };
  }

  onClose(handler) {
    this.closeHandlers.push(handler);
    return () => {
      const index = this.closeHandlers.indexOf(handler);
      if (index > -1) this.closeHandlers.splice(index, 1);
    };
  }

  close() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }
}

let wsClient = null;

export function getWebSocketClient(endpoint) {
  if (!wsClient || !wsClient.isConnected()) {
    wsClient = new AgentWebSocket(endpoint);
  }
  return wsClient;
}

export function closeWebSocket() {
  if (wsClient) {
    wsClient.close();
    wsClient = null;
  }
}
